<template>
  <div>
    <div class="container mx-auto mt-7 mb-7">
      <div
        class="flex border-l-4 border-orange-600 shadow py-3 px-4 mb-6 rounded"
        v-if="showAnnouncement"
        role="alert"
      >
        <span
          class="material-icons text-3xl"
          style="color: #dd6b20"
          aria-hidden
        >
          report_problem
        </span>
        <div class="mx-3 flex-grow">
          <h4 class="font-bold font-sans text-sm pb-1">
            {{ announcementData.heading }}
          </h4>
          <p class="font-sans text-sm">
            {{ announcementData.body }}
          </p>
        </div>
        <span
          class="material-icons self-center text-xl"
          style="color: #dd6b20"
          role="button"
          aria-label="cancel announcement"
          @click="announcementData = {}"
        >
          cancel
        </span>
      </div>
      <div class="mx-4">
        <div class="md:flex md:flex-row md:items-center py-px">
          <div class="w-full md:w-1/2 mb-4 md:mb-0">
            <h2
              class="lg:leading-tight lg:text-6xl block font-sans font-semibold leading-none max-w-4xl subpixel-antialiased text-4xl text-blue-900"
            >
              Select your service
            </h2>
          </div>
          <div class="w-full md:w-1/2">
            <p class="md:text-right">
              <doe-button
                v-if="hasDirectorRights || isVerifiedDirector"
                appearance="primary"
                size="small"
                suffix="plus"
                iconSize="small"
                @click="newService()"
              >
                Register service
              </doe-button>
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="container mx-auto">
      <div class="mx-4">
        <div class="border-l-2 border-red-900 py-2 px-2 md:px-4 max-w-4xl mb-6">
          <h3 class="block font-sans font-semibold leading-snug max-w-4xl subpixel-antialiased text-2xl text-blue-900 lg:leading-snug">Important Information</h3>
          <p class="font-sans text-blue-900 mt-2 text-base"><strong>Please note there may be a short delay in your registered service appearing on this dashboard. If your registered service doesn’t appear within 15 minutes and after refreshing your screen please contact the department’s Information and Enquiries team on 1800 619 113 (toll free) or at <a href="mailto:information@earlylearningcommission.nsw.gov.au" class="underline">information@earlylearningcommission.nsw.gov.au</a>.</strong></p>
        </div>
        <div v-if="rows.length">
          <doe-table
            dataset="Service"
            sortKey="seNumber"
            :actions="actions"
            :columns="columns"
            :settings="settings"
            :rows="services"
            :hasActionBoxes="false"
            :isSearchable="true"
            @action:statements="actionStatements($event)"
            @action:users="actionUsers($event)"
          />
        </div>

        <div v-else class="border-t border-gray-500 pt-7">
          <h2
            class="block font-sans font-semibold leading-snug max-w-4xl mb-4 subpixel-antialiased text-3xl text-blue-900"
          >
            Welcome to your service dashboard
          </h2>
          <ul class="list-disc mb-5 pl-4">
            <li
              class="font-sans font-light max-w-4xl text-base text-black leading-loose mb-2"
            >
              If you are a centre director or nominated supervisor, use the Register Service button below.
            </li>
            <li
              class="font-sans font-light max-w-4xl text-base text-black leading-loose mb-2"
            >
              If you are an early childhood teacher or educator, please contact your centre director to request access.
            </li>
            <li
              class="font-sans font-light max-w-4xl text-base text-black leading-loose"
            >
              For further information or support, please see the
              <a
                class="underline"
                href="/assets/Self Assessment and Quality Improvement Planning Portal User Guide.pdf"
                target="_blank">User Guide<span class="sr-only">Opens external website</span></a
              >
              or contact the department's Information and enquiries team.
            </li>
          </ul>
          <p
            v-if="hasDirectorRights || isVerifiedDirector"
            class="font-sans font-light max-w-4xl text-base text-black leading-loose mb-6"
          >
            A centre director or nominated supervisor needs to register to be
            the service administrator on the Self-Assessment and Quality Improvement Planning Portal platform before educator access can be granted. The
            administrator is responsible for managing access for users
            (educators) at the service.
          </p>
          <doe-button
            v-if="hasDirectorRights || isVerifiedDirector"
            appearance="primary"
            size="large"
            suffix="plus"
            iconSize="medium"
            @click="newService()"
          >
            Register service
          </doe-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { serviceCallWithAuth } from "@/helpers/cognito";
import handleRoles from "@/mixins/handleRoles";
import { COLUMNS, ACTIONS, SETTINGS } from "@/data/serviceTable";
import DoeTable from "@/components/table/Table";
import DoeButton from "@/components/button/Button";
import axios from 'axios';

dayjs.extend(customParseFormat);

export default {
  name: "services",
  mixins: [handleRoles],
  inject: {
    $validator: "$validator"
  },
  components: {
    DoeTable,
    DoeButton
  },
  data() {
    return {
      columns: COLUMNS,
      actions: ACTIONS,
      settings: SETTINGS,
      selectedService: "",
      services: [],
      q: "",
      announcementData: {}
    };
  },
  async mounted() {
    this.loadData()
  },
  methods: {
    ...mapActions(["SET_SERVICE","CLEAR_SERVICE"]),
    loadData() {
      this.$root.$emit('refreshtoken')
      this.$root.isLoading = true;
      this.CLEAR_SERVICE();
      const resp = this.getRefData;

      if (resp && resp.banner && resp.banner.ece) {
        this.announcementData = { ...resp.banner.ece };
      }
      let seNumbers = encodeURIComponent(this.mapSeNumbers);

       let headers = {
            ...this.$OAuth.buildHttpHeader()
        } 

      let options = { path: "/centre", headers: headers };

      serviceCallWithAuth("doe-ttss", options)
        .then(async results => {
          const unique = {};
          const array = seNumbers.split("%3A");
          results.body = results.body.filter(e=>(e.serviceId!=='SE-00000000'))
          results.body.forEach(el=>{
            el.seNumber=el.serviceId;
          })
          this.services = results.body;

          // Supplement with services registered in portal DB but not yet in JWT ACL
          await this.loadPortalDbServices();

          // add all the services for the user to DB 

          let userid = this.profile.detuserid;
          console.log("array...."+array.toString())
          const headers = {
            ...this.$OAuth.buildHttpHeader(),
            'isportal': true,
            'ssotoken' : localStorage.getItem("id_token"),
        } 
      
          const endpoint = `${this.$config.serverUrl}/api/user-details/addservices`
          console.log("endpoint"+endpoint)
          console.log("userid...."+userid)

         
           axios.post(endpoint,{'userId':userid, 'servicelist':array.toString()} , {headers}).then(function (response) {            console.log(response)
          }).catch(function (error) {
            console.log(error);
          });
      
                   

        })
        .catch((err) => {
          const errorMessage = err.message === 'Failed to fetch' ? 'Service cannot be reached' : "Your service can't be registered";
          this.$root.translateModal(
            "For more information, please contact the department’s Information and Enquiries team on 1800 619 113 (toll free) or at information@earlylearningcommission.nsw.gov.au.",
            errorMessage
          );
        })
        .finally(() => {
          this.$root.isLoading = false;
        });
    },
    async actionStatements(event) {
      var selectedService = this.services.filter(service => {
        return service.seNumber == event.seNumber;
      });
      if (selectedService.length) {
        this.SET_SERVICE(selectedService[0]);
        const headers = {
            ...this.$OAuth.buildHttpHeader(),
            'isportal': true,
            'ssotoken' : localStorage.getItem("id_token"),
        } 
        if(!selectedService[0].portalInitialised) {
          const endpoint = `${this.$config.serverUrl}/api/asf-portl-element-data/${event.seNumber}/initialise`
          await axios.post(endpoint, {}, {headers})
        }
        window.location = this.$config.portalUrl + '/main/qiip/dashboard?serviceId=' + event.seNumber
        //this.$router.push({ name: "landing" });
      }
    },
    actionUsers(event) {
      var selectedService = this.services.filter(service => {
        return service.seNumber == event.seNumber;
      });
      if (selectedService.length) {
        this.SET_SERVICE(selectedService[0]);
        this.$router.push({ name: "user-dashboard" });
      }
    },
    selectService() {
      this.SET_SERVICE(this.selectedService);
      this.$router.push({ name: "statement-dashboard" });
    },
    newService() {
      this.$router.push({ name: "service-register" });
    },
    async loadPortalDbServices() {
      try {
        const userId = this.profile.detuserid;
        const headers = {
          'isportal': true,
          'ssotoken': localStorage.getItem("id_token"),
          'Content-Type': 'application/json'
        };
        const response = await axios.get(`${this.$config.serverUrl}/api/user-details/getServices/${userId}`, { headers });
        const dbServiceIds = response.data.servicelist
          ? response.data.servicelist.split(',').filter(id => id && id !== 'SE-00000000')
          : [];
        const existingIds = new Set(this.services.map(s => s.seNumber));
        const missingIds = dbServiceIds.filter(id => !existingIds.has(id));
        for (const seNumber of missingIds) {
          try {
            const resp = await axios.get(`${this.$config.serverUrl}/api/service-details/${seNumber}`, { headers });
            if (resp.data) {
              this.services = [...this.services, { ...resp.data, seNumber: resp.data.serviceId || seNumber }];
            }
          } catch (e) {
            console.log(`Could not fetch details for service ${seNumber}`, e);
          }
        }
      } catch (e) {
        console.log('Could not load portal DB services', e);
      }
    }
  },
  computed: {
    ...mapGetters(["authUser", "idToken", "getRefData"]),
    mapSeNumbers() {
      //let seNumbers = this.$OAuth.data.profile.acl.map(
      const acls = Array.isArray(this.$OAuth.data.profile.acl)
        ? this.$OAuth.data.profile.acl
        : [this.$OAuth.data.profile.acl];
      let seNumbers = acls.map(
        row => row.split(" ")[0]
      );
      return [...new Set(seNumbers)].join(":");
    },
     profile() {
      return this.$OAuth.data.profile;
    },
    rows() {
      return this.services;
    },
    showAnnouncement() {
      if (Object.keys(this.announcementData).length) {
        const { displayStartTime, displayEndTime } = this.announcementData;
        const startTimestamp = dayjs(
          displayStartTime,
          "DD/MM/YYYY HH:mm:ss"
        ).valueOf();
        const endTimestamp = dayjs(
          displayEndTime,
          "DD/MM/YYYY HH:mm:ss"
        ).valueOf();
        const nowTimestamp = Date.now();
        return startTimestamp <= nowTimestamp && endTimestamp > nowTimestamp;
      }
      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
li /deep/ a {
  @apply inline-block relative underline;
}
li /deep/ a[href*="//"]:not([href*="localhost.com"]) {
  padding-right: 1.5rem;
  &:after {
    @apply absolute block;
    content: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 100 89'><path d='M100,4.16666667 L100,26.3861111 C100,30.1119792 95.4930556,31.9381944 92.8869792,29.3324653 L86.6878472,23.1329861 L44.4095486,65.4112847 C42.7822917,67.0385417 40.1442708,67.0385417 38.5170139,65.4112847 L34.5887153,61.4829861 C32.9614583,59.8557292 32.9614583,57.2175347 34.5887153,55.5904514 L76.8673611,13.3118056 L70.6689236,7.11302083 C68.0524306,4.49652778 69.9055556,0 73.6152778,0 L95.8333333,0 C98.1345486,0 100,1.86545139 100,4.16666667 Z M70.6647569,47.0128472 L67.8869792,49.790625 C67.1055707,50.5720468 66.6666073,51.6318913 66.6666667,52.7369792 L66.6666667,77.7777778 L11.1111111,77.7777778 L11.1111111,22.2222222 L56.9444444,22.2222222 C58.049457,22.2221592 59.1092025,21.783211 59.890625,21.0019097 L62.6684028,18.2241319 C65.2932292,15.5991319 63.4342014,11.1111111 59.7222222,11.1111111 L8.33333333,11.1111111 C3.73090278,11.1111111 0,14.8420139 0,19.4444444 L0,80.5555556 C0,85.1579861 3.73090278,88.8888889 8.33333333,88.8888889 L69.4444444,88.8888889 C74.046875,88.8888889 77.7777778,85.1579861 77.7777778,80.5555556 L77.7777778,49.9590278 C77.7777778,46.246875 73.2895833,44.3878472 70.6647569,47.0128472 Z' /></svg>");
    top: 0px;
    right: 0px;
    width: 1.25rem;
    height: 1.25rem;
  }
}
</style>
